import {
	useDeleteOpportunities,
	useDeleteOpportunitiesByFilter,
	useExportOpportunitiesMutation,
	useOpportunityContactsMutation,
} from '@Queries/index';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { useSelectionState } from '../../../../../hooks/useSelectionState';
import { useFullscreenModal, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { numberToCurrencyStringValue } from '../../../../../models/UiUtils';
import { invalidateAllBoardStageItems } from '../../../../../queries/Boards/useBoardStageItems';
import { invalidateAllBoardStageItemsSearch } from '../../../../../queries/Boards/useBoardStageItemsSearch';
import { invalidateAllBoardStageItemsTotals } from '../../../../../queries/Boards/useBoardStageItemsTotals';
import { invalidateAllGetBoardItems } from '../../../../../queries/Boards/useGetBoardItems';
import { IBoard } from '../../../../../viewmodels/AppViewModels';
import { useOpportunitiesContext } from '../../../../containers/dataBoards/OpportunitiesBoard/context';
import { styleSheet as dbStyleSheet } from '../../../../containers/dataBoards/styles';
import { styleSheet as newDbStyleSheet } from '../../../../pages/DataBoards/styles';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { DataboardActions, DataboardBody } from '../../../Databoard/Databoard';
import { Button } from '../../../../components-v2/Button';
import { Pill } from '../../../../components-v2/Pill/Pill';
import { DeleteConfirmation } from '../../../DeleteConfirmation';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../HoverCard';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../DataGrid';
import { FullscreenModalNavLink } from '../../../fullscreen/FullscreenModalNavLink';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { UsersActionBar } from '../../../UsersActionBar';
import { buildBulkDeleteRequest } from './buildBulkDeleteRequest';
import { styleSheet } from './styles';

const _OpportunitiesTable = ({
	board,
	opportunityFilterCriteria,
	onAddOpportunityClicked,
}: {
	board: IBoard;
	opportunityFilterCriteria: Api.IOpportunitySearchRequest;
	onAddOpportunityClicked?: () => void;
}) => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const opportunityContactsMutation = useOpportunityContactsMutation();
	const { opportunities, isLoading } = useOpportunitiesContext();
	const { logApiError } = useEventLogging('OportunitiesTable');

	const [actionButtonsDisabled, setActionButtonsDisabled] = React.useState(false);
	const exportOpportunitiesMutation = useExportOpportunitiesMutation();

	const {
		isSelectionStateAll,
		selectedRows,
		excludedRows,
		isPartialSelected,
		isAllSelected,
		selectionState,
		isSelected,
		reset,
		toggleRow,
		toggleSelectAll,
	} = useSelectionState({ rows: opportunities?.map(op => op.id) });

	const onExport = () => {
		exportOpportunitiesMutation.mutate({
			filterRequest: opportunityFilterCriteria,
			includeActivity: false,
			boardId: board.id,
		});
	};

	const [showingDeleteConfirmation, setShowingDeleteConfirmation] = React.useState(false);

	const selectedCount = selectedRows.length;

	const onDeleteSuccess = (deletedCount: number) => {
		setShowingDeleteConfirmation(false);
		reset();
		invalidateAllBoardStageItems();
		invalidateAllBoardStageItemsSearch();
		invalidateAllBoardStageItemsTotals();
		invalidateAllGetBoardItems();
		toast.success(`${deletedCount} ${deletedCount === 1 ? 'opportunity' : 'opportunities'} deleted`);
	};

	const onDeleteError = (error: Api.IOperationResultNoValue) => {
		setShowingDeleteConfirmation(false);
		logApiError('DeleteOpportunities-Error', error);
		toast.error(error?.systemMessage ?? 'Failed to delete opportunities');
	};

	const deleteOpportunitiesMutation = useDeleteOpportunities({
		onSuccess: onDeleteSuccess,
		onError: onDeleteError,
	});

	const deleteOpportunitiesByFilterMutation = useDeleteOpportunitiesByFilter({
		onSuccess: onDeleteSuccess,
		onError: onDeleteError,
	});

	const onDeleteFinished = (shouldDelete: boolean) => {
		if (!shouldDelete) {
			setShowingDeleteConfirmation(false);
			return;
		}

		const request = buildBulkDeleteRequest({
			isAllSelected,
			selectedRows,
			excludedRows,
			filter: opportunityFilterCriteria,
		});

		if (!request) {
			setShowingDeleteConfirmation(false);
			return;
		}

		if (request.kind === 'ids') {
			deleteOpportunitiesMutation.mutate({ itemIds: request.itemIds });
			return;
		}

		deleteOpportunitiesByFilterMutation.mutate({
			boardId: board.id,
			filter: request.filter,
			stageId: request.stageId,
			excludeItemIds: request.excludeItemIds,
			confirmWholeBoardDelete: request.confirmWholeBoardDelete,
		});
	};

	const excludedContacts = React.useMemo(() => {
		return excludedRows
			.map(opportunityId => {
				const opportunity = opportunities.find(o => o.id === opportunityId);
				return opportunity ? opportunity.primaryContact : null;
			})
			.filter(Boolean) as Api.IProjectedContact[];
	}, [excludedRows, opportunities]);

	const getMatchingContacts = async () => {
		setActionButtonsDisabled(true);
		let results: Api.IProjectedContact[] = [];

		if (isAllSelected) {
			try {
				const apiResults = await opportunityContactsMutation.mutateAsync({
					filterRequest: opportunityFilterCriteria || {},
					boardId: board.id,
				});

				const excludedContactIdsMap = new Set(excludedContacts.map(contact => contact.id));
				results = apiResults.filter(contact => !excludedContactIdsMap.has(contact.id));
			} catch (error) {
				logApiError('ResolveContacts-Error', error);
			}
		} else {
			results = opportunities
				.filter(opportunity => selectedRows.indexOf(opportunity.id) > -1)
				.map(opportunity => opportunity.primaryContact as Api.IProjectedContact);
		}

		setActionButtonsDisabled(false);
		return results;
	};

	const onSendEmailClicked = async () => {
		const matchingContacts = await getMatchingContacts();

		fullscreenModal.history?.push({
			pathname: '/email/campaigns/create/from-selection',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						bulkFilterRequestOptions: {
							readOnlySearchCriteria: true,
						},
						contacts: matchingContacts.map(c => new Api.ContactViewModel(userSession, c)),
					},
					type: 'Contacts',
				},
			},
		});
	};

	return (
		<div className={css(styleSheet.container)}>
			<DataboardActions>
				<div className={css(dbStyleSheet.leftHeader, styleSheet.actionBarLeft)}>
					<UsersActionBar
						getMatchingContacts={getMatchingContacts}
						onExport={onExport}
						selectionState={selectionState}
						actionButtonsDisabled={actionButtonsDisabled}
						onSendEmailClicked={onSendEmailClicked}
						onDeleteClicked={() => setShowingDeleteConfirmation(true)}
						deleteDisabled={selectionState === Api.ESelectionState.None}
					/>
				</div>
				{onAddOpportunityClicked ? (
					<Button
						variant='primary'
						size='sm'
						styles={[styleSheet.addOpportunityButton]}
						onClick={onAddOpportunityClicked}
					>
						+ Add Opportunity
					</Button>
				) : null}
			</DataboardActions>
			<DataboardBody styles={[styleSheet.content]}>
				<DataGrid styles={[styleSheet.tableOverrides]}>
					<DataGridColumns>
						<DataGridColumn sticky>
							<Checkbox
								checked={isSelectionStateAll}
								id='select-all-opportunities'
								onChange={toggleSelectAll}
								partial={isPartialSelected}
								disabled={opportunities.length === 0}
							/>
						</DataGridColumn>
						<DataGridColumn sticky>Opportunity Name</DataGridColumn>
						<DataGridColumn sticky>Stage</DataGridColumn>
						<DataGridColumn sticky>Amount</DataGridColumn>
						<DataGridColumn sticky>Owner</DataGridColumn>
						<DataGridColumn sticky>Close Date</DataGridColumn>
						<DataGridColumn sticky>Company</DataGridColumn>
						<DataGridColumn sticky>Tags</DataGridColumn>
					</DataGridColumns>

					<DataGridRows>
						{!isLoading
							? opportunities.map(opportunity => {
									const closeMoment = moment(opportunity.closeDate);
									const owner = opportunity.assignees[0] ?? null;
									return (
										<DataGridRow key={`opportunity-${opportunity.id}`} styles={[newDbStyleSheet.row]}>
											<DataGridRowItem>
												<Checkbox
													readOnly
													checked={isSelected(opportunity.id)}
													id={opportunity.id}
													onClick={event => {
														event.stopPropagation();
														event.preventDefault();
														toggleRow(opportunity.id, event.shiftKey);
													}}
												/>
											</DataGridRowItem>
											<DataGridRowItem styles={[styleSheet.nameColumn]}>
												<FullscreenModalNavLink
													title={opportunity.name}
													to={{
														pathname: `/dataBoards/opportunities/${board.id}/${opportunity.id}`,
													}}
												>
													<h6 className={css(styleSheet.name, baseStyleSheet.truncateText, baseStyleSheet.fontBold)}>
														{opportunity.name}
													</h6>
												</FullscreenModalNavLink>
											</DataGridRowItem>
											<DataGridRowItem>{opportunity.boardStage ? opportunity.boardStage.name : ''}</DataGridRowItem>
											<DataGridRowItem>${numberToCurrencyStringValue(opportunity.dealSize)}</DataGridRowItem>
											<DataGridRowItem>{owner ? getDisplayName(owner, true) : ''}</DataGridRowItem>
											<DataGridRowItem>
												{closeMoment.isValid() ? closeMoment.format('MM/DD/YYYY') : 'No Date'}
											</DataGridRowItem>
											<DataGridRowItem>{opportunity.company ? opportunity.company.companyName : null}</DataGridRowItem>
											<DataGridRowItem>
												<div className={css(styleSheet.tagsColumn)}>
													{(() => {
														// Use tagsCollection if available (with colors), otherwise fall back to tags array
														const displayTags =
															opportunity.tagsCollection && opportunity.tagsCollection.length > 0
																? opportunity.tagsCollection
																: opportunity.tags?.map(tagName => ({
																		name: tagName,
																		id: tagName,
																		color: undefined as Api.IAccountTag['color'],
																	})) || [];

														if (displayTags.length > 0) {
															return (
																<>
																	<Pill
																		variant='tag'
																		label={displayTags[0].name}
																		color={'color' in displayTags[0] ? displayTags[0].color : undefined}
																	/>
																	{displayTags.length > 1 && (
																		<HoverCard>
																			<HoverCardTrigger className={css(baseStyleSheet.brandLink, styleSheet.tagMore)}>
																				&nbsp;{`+${displayTags.length - 1} more`}
																			</HoverCardTrigger>
																			<HoverCardContent styles={[styleSheet.extraTags]}>
																				{displayTags.slice(1).map(tagValue => {
																					return (
																						<Pill
																							key={'id' in tagValue ? tagValue.id : tagValue.name}
																							variant='tag'
																							label={tagValue.name}
																							color={'color' in tagValue ? tagValue.color : undefined}
																						/>
																					);
																				})}
																			</HoverCardContent>
																		</HoverCard>
																	)}
																</>
															);
														}
														return null;
													})()}
												</div>
											</DataGridRowItem>
										</DataGridRow>
									);
								})
							: null}

						{isLoading && (
							<DataGridRow>
								<DataGridRowItem fullColSpan>
									<LoadingSpinner className={css(styleSheet.loading, baseStyleSheet.absoluteCenter)} type='large' />
								</DataGridRowItem>
							</DataGridRow>
						)}
					</DataGridRows>
				</DataGrid>
			</DataboardBody>
			<DeleteConfirmation
				isOpen={showingDeleteConfirmation}
				onFinish={onDeleteFinished}
				title='Delete opportunities?'
				destructiveButtonText='Delete'
				showSpinner={deleteOpportunitiesMutation.isPending || deleteOpportunitiesByFilterMutation.isPending}
				bodyText={
					isAllSelected
						? excludedRows.length > 0
							? `This permanently deletes every opportunity matching your current filters, excluding the ${excludedRows.length} ${
									excludedRows.length === 1 ? 'row' : 'rows'
								} you unchecked. Archived opportunities are not affected. This cannot be undone.`
							: 'This permanently deletes every opportunity matching your current filters. Archived opportunities are not affected. This cannot be undone.'
						: `This permanently deletes ${selectedCount} ${
								selectedCount === 1 ? 'opportunity' : 'opportunities'
							}. This cannot be undone.`
				}
			/>
		</div>
	);
};

export const OpportunitiesTable = observer(_OpportunitiesTable);
